import { useReducer, useEffect } from "react";
import useLocalStore from "@/hooks/useLocalStore";
import { getTranslation, subscribeToLocale } from "@/i18n";
import {
    getCached,
    fetchTranslation,
    subscribeToTranslations,
    isLoading,
} from "@/i18n/googleTranslate";

/**
 * Returns a `t(key)` function that resolves translated text.
 *
 * Resolution order:
 *   1. Static locale file for the current language  (instant once loaded)
 *   2. Google Translate cache                        (instant after first fetch)
 *   3. Google Translate API fetch                    (async – renders key first,
 *                                                      then re-renders with translation)
 *   4. Raw key as fallback
 *
 * @example
 *   const { t } = useTranslation();
 *   t("nav.about")        // static entry → "About" | "À propos" | …
 *   t("Some other text")  // not in file → fetched from Google Translate
 */
const useTranslation = () => {
    const language = useLocalStore((s) => s.language) || "en";

    // Re-render when either the locale file or the Google cache updates
    const forceUpdate = useReducer((x) => x + 1, 0)[1];

    useEffect(() => {
        const unsubLocale = subscribeToLocale(forceUpdate);
        const unsubGoogle = subscribeToTranslations(forceUpdate);
        return () => {
            unsubLocale();
            unsubGoogle();
        };
    }, [forceUpdate]);

    const t = (key) => {
        // 1. Static locale file (en bundled; others loaded on demand)
        const staticValue = getTranslation(key, language);
        if (staticValue !== key) return staticValue;

        // 2 & 3. Google Translate (for keys not in the static files)
        if (language === "en") {
            fetchTranslation(key, "en");
            return key;
        }

        const cached = getCached(key, language);
        if (cached) return cached;

        fetchTranslation(key, language);
        return key;
    };

    const isKeyLoading = (key) => {
        const staticValue = getTranslation(key, language);
        if (staticValue !== key || language === "en") return false;
        return isLoading(key, language);
    };

    return { t, language, isKeyLoading };
};

export default useTranslation;
